<template>
  <StoryblokComponent v-if="story" :blok="story.content" :key="apiUrl" />
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import type { ISbStoryData } from 'storyblok-js-client';
import { useRoute } from 'vue-router';
import { useMeta } from '~/composables/useMeta';
import { STORY_RELATIONS } from '~/composables/relations';
import type { HeroBlockProps } from '~/types/HeroBlock';
import { optimizeStoryContentCollections } from '~/utils/optimizeStoryData';
import type { SbBlock } from '~/types/common';

// Note: keepalive causes issues with SPA navigation and StoryBlok page rendering
// by caching instances of the component
definePageMeta({ keepalive: false, middleware: ['redirects'] });

const {
  app: { isDevEnv },
  public: {
    websiteFolder,
    publishState,
    storyBlokApiKey,
    defaultLanguage,
    supportedLanguages,
    siteUrl,
  },
} = useRuntimeConfig();

const route = useRoute();
const language = useState<string>('language');

const locale = computed(() => {
  return language.value && supportedLanguages.includes(language.value)
    ? language.value
    : defaultLanguage;
});

const isEditMode = !!route.query._storyblok;

const apiUrl = computed(() => {
  if (isEditMode) {
    // If edit mode, url is in form of /preview/?path=...
    return String(route.query.path || `/${websiteFolder}`);
  }

  const [lang, ...rest] = route.path.split('/').filter(Boolean);
  return lang && supportedLanguages.includes(lang)
    ? `${lang}/${rest.join('/')}`
    : `${defaultLanguage}${route.path}`;
});

const apiOptions = {
  version: isEditMode ? 'draft' : (publishState as 'draft' | 'published'),
  token: storyBlokApiKey,
  resolve_relations: STORY_RELATIONS,
  language: locale.value,
};

// Note: useState('story') is needed to provide the story to other components
const story = useState<ISbStoryData>('story');

// Note: prevent reloading story already passed via SSR
if (!story.value) {
  try {
    const storyblokApi = useStoryblokApi();
    const { data } = await storyblokApi.get(`cdn/stories/${apiUrl.value}`, apiOptions);
    story.value = optimizeStoryContentCollections(data?.story);
  } catch (error) {
    // Note: Vercel deployments log request errors automatically
    if (isDevEnv) {
      console.error(`Cannot find StoryBlock record with path "${apiUrl.value}"`);
    }
  }
}

if (!story.value) {
  throw createError({
    status: 404,
    statusText: 'Page not found',
  });
}

const { data: breadcrumbs } = await useFetch('/api/breadcrumbs', {
  query: { uuid: story.value.uuid },
});

const pageLang = computed(() => story.value?.full_slug?.split('/')?.[0]);

const extractHeroMeta = (contentBody: (SbBlock | HeroBlockProps)[] = []) => {
  const heroBlock = (contentBody[0] || {}) as HeroBlockProps;
  // Only use Hero component for extracting meta
  if (heroBlock.component !== 'Hero') {
    return {
      title: '',
      description: '',
      background: '',
    };
  }

  const background = heroBlock.asset?.filename?.includes('/image') ? heroBlock.asset?.filename : '';
  const headings = heroBlock.heading || [];
  const headingTitle = headings.find((headingLine) => headingLine.size === 'large')?.text || '';
  const description = headings.find((headingLine) => headingLine.size !== 'large')?.text || '';

  return {
    title: headingTitle || heroBlock.title,
    description,
    background,
  };
};

const storySeo = computed(() => {
  let storySEO = story.value?.content?.SEO || {};
  const content = story.value?.content || {};

  // Read values from hero block and use in case specific seo overrides are not set
  const heroMeta = extractHeroMeta(content.body);

  const title = story.value.content?.summaryTitle || heroMeta.title || story.value.name;
  const excerpt = story.value.content?.excerpt;
  const description = excerpt?.length ? excerpt : heroMeta.description;
  // Limit image dimensions to FB recommended (if file size over 8Mb it gets discarded)
  const storyImage = storySEO.og_image || content.thumbnail?.filename || heroMeta.background;
  const storyImageLimited = storyImage.replace(
    'image/upload/',
    'image/upload/f_auto,q_auto,g_auto,w_1200,c_fill/',
  );
  // Note: if image is empty, FB will pick up random (wrong) images from the page source
  const defaultLogoImage = `${siteUrl}/og-fib-logo.png`;
  const image = storyImage ? storyImageLimited : defaultLogoImage;

  /**
   * for unknown reasons, some stories have `SEO: ""` saved in their JSON,
   * so covering this edge case
   */
  if (typeof storySEO === 'string') {
    storySEO = {
      title,
      description,
    };
  }
  if (!storySEO?.title) {
    storySEO.title = title;
  }
  if (!storySEO.description) {
    storySEO.description = description;
  }

  // Open graph
  storySEO.og_image = image; // replace original with version with limited dimensions
  if (!storySEO.og_title) {
    storySEO.og_title = storySEO.title;
  }
  if (!storySEO.og_description) {
    storySEO.og_description = storySEO.description;
  }
  // Twitter
  if (!storySEO.twitter_image) {
    storySEO.twitter_image = storySEO.og_image;
  }
  if (!storySEO.twitter_description) {
    storySEO.twitter_description = storySEO.og_description;
  }
  if (!storySEO.twitter_title) {
    storySEO.twitter_title = storySEO.og_title;
  }

  storySEO.og_url = `${siteUrl}${route.fullPath}`;

  return storySEO;
});

// Set initial data during SSR
// these values will not change during SPA navigation as they already have values
useMeta(storySeo.value, pageLang.value);
const alternates = useState('alternates', () => story.value?.alternates || []);
const headerTheme = useState('header_theme', () => story.value?.content?.header_theme || 'DARK');
const storyType = useState('storyType', () => story.value?.content?.component || 'page');
const crumbs = useState('breadcrumbs', () => breadcrumbs.value || []);
const isMobileNavOpen = useState('isMobileNavOpen');

onMounted(async () => {
  // Connect StoryBlok editor to the page
  // (enables clicking on block in preview mode to edit it)
  useStoryblokBridge(
    story.value.id,
    (evStory) => (story.value = evStory),
    { resolveRelations: STORY_RELATIONS, resolveLinks: 'url' }, // Bridge Options
  );
  /*
   * We had to disable SPA navigation due to it conflicting with the Nitro static-page caching strategy.
   */
  // Make sure states are updated after page is mounted by manualy changing state values
  // useMeta(storySeo.value, pageLang.value);
  // alternates.value = story.value?.alternates || [];
  // headerTheme.value = story.value?.content?.header_theme || 'DARK';
  // storyType.value = story.value?.content?.component || 'page';
  // crumbs.value = getBreadcrumbs(linksRef.value, story.value) || [];
  // isMobileNavOpen.value = false;
});
</script>
